import { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/site/Reveal";
import { ContactForm } from "@/components/site/ContactForm";
import { STAFF, DIVISIONS, IMAGES, LOGO_MARK, slugify } from "@/lib/data";

const initials = (name) =>
  name.replace(/&/g, " ").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const RosterRow = ({ person, index, displayNum }) => {
  const num = String(displayNum ?? index + 1).padStart(2, "0");
  return (
    <Reveal>
      <div
        id={slugify(person.name)}
        data-testid={`staff-card-${index}`}
        className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 py-12 border-t border-white/10 hover:bg-white/[0.02] transition-colors duration-300 scroll-mt-28"
      >
        <div className="lg:col-span-3">
          <div className="relative aspect-[4/5] max-w-[260px] overflow-hidden bg-[#111] border border-white/10">
            {person.image ? (
              <img
                src={person.image}
                alt={person.name}
                className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-700 ease-out"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="font-display text-7xl text-white/15 group-hover:text-[#cbd1d8] transition-colors duration-500">{initials(person.name)}</span>
              </div>
            )}
            <span className="absolute top-3 left-3 font-display text-3xl text-white mix-blend-difference">{num}</span>
          </div>
        </div>
        <div className="lg:col-span-4 flex flex-col justify-center">
          <span className="inline-block self-start text-[10px] uppercase tracking-[0.22em] text-black bg-[#cbd1d8] px-3 py-1 mb-4">{person.category}</span>
          <h3 className="font-display text-4xl lg:text-5xl leading-[0.9] text-white">{person.name}</h3>
          <p className="text-[#cbd1d8] uppercase tracking-[0.16em] text-xs mt-3">{person.role}</p>
        </div>
        <div className="lg:col-span-5 flex items-center">
          <p className="text-white/60 text-base lg:text-lg leading-relaxed whitespace-pre-line">{person.bio}</p>
        </div>
      </div>
    </Reveal>
  );
};

export default function Team() {
  const navigate = useNavigate();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.lenis) window.lenis.scrollTo(el, { offset: -100 });
      else el.scrollIntoView({ behavior: "smooth" });
    }, 450);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="team-page">
      {/* header */}
      <section className="relative pt-40 pb-24 lg:pt-52 lg:pb-32 overflow-hidden bg-black">
        <div className="absolute inset-0">
          <img src={IMAGES.staffHero} alt="" className="w-full h-full object-cover grayscale opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/70 to-black/50" />
        </div>
        <img src={LOGO_MARK} alt="" className="absolute -right-10 top-20 h-[420px] w-auto opacity-[0.06] pointer-events-none hidden lg:block" />
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#cbd1d8]" />
            <span className="text-[12px] uppercase tracking-[0.32em] text-[#cbd1d8]">The People</span>
          </motion.div>
          <MaskedLines
            lines={["MEET THE", "TEAM."]}
            className="font-display leading-[0.82] text-white text-[18vw] sm:text-[15vw] lg:text-[12vw]"
            delay={0.2}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="text-white/60 text-lg max-w-xl mt-8 leading-relaxed"
          >
            The giant-slayers behind every deal — negotiators, strategists, marketers and partners who put athletes and families first.
          </motion.p>
        </div>
      </section>

      {/* roster */}
      <section data-testid="staff-grid" className="py-16 lg:py-24 bg-[#050505]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          {(() => {
            const byName = Object.fromEntries(STAFF.map((p) => [p.name, p]));
            let gi = 0;
            return DIVISIONS.map((div) => {
              const members = div.members.map((n) => byName[n]).filter(Boolean);
              if (!members.length) return null;
              return (
                <div key={div.title} data-testid={`division-${slugify(div.title)}`} className="mb-6">
                  <Reveal className="flex items-baseline gap-4 pt-14 pb-1">
                    <h3 className="font-display text-4xl lg:text-6xl text-[#cbd1d8] uppercase tracking-wide leading-none">{div.title}</h3>
                    <span className="text-white/35 text-sm">/ {String(members.length).padStart(2, "0")}</span>
                  </Reveal>
                  <div className="border-b border-white/10">
                    {members.map((p, j) => {
                      const idx = gi++;
                      return <RosterRow key={p.name} person={p} index={idx} displayNum={j + 1} />;
                    })}
                  </div>
                </div>
              );
            });
          })()}

          <Reveal className="mt-24 border border-white/10 p-12 lg:p-20 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div>
              <h3 className="font-display text-5xl lg:text-6xl text-white leading-none">Want to join the fight?</h3>
              <p className="text-white/55 text-lg mt-4 max-w-xl">We're always looking for relentless people who believe in the underdog.</p>
            </div>
            <button
              data-testid="team-contact-cta"
              onClick={() => navigate("/#contact")}
              className="group inline-flex items-center gap-3 bg-[#cbd1d8] text-black font-bold uppercase tracking-[0.14em] text-sm px-10 py-4 hover:bg-white transition-colors duration-300 shrink-0"
            >
              Get In Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </Reveal>
        </div>
      </section>

      <ContactForm />
    </div>
  );
}
